import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';

import { cloneDeep } from 'es-toolkit';
import { useRentalManagerUserPropertyFloorPlanEditWebApiHandler_dateAvailable } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../Wizard/actions';

import { isValidDateStringFormatMMDDYYYY } from '../../../../utils/dateUtils';

import DatePickerWithDateSelect from '../../../DatePickerWithDateSelect';
import WizardAlert from '../../../WizardAlert';

const fieldName = 'dateAvailable';

const DateAvailableFormField = ({ showNextMonthButton = true }) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, shouldSaveOnBlur, isInModalState, isSaveComplete },
    reducerDispatch,
  } = useContext(ModularListingContext);
  const { floorplans = [], floorplans: [{ dateAvailable, floorPlanId } = {}] = [] } = useSelector(
    (state) => state.properties.currentProperty,
  );

  const { mutateAsync: updateDateAvailable } = useRentalManagerUserPropertyFloorPlanEditWebApiHandler_dateAvailable();

  const [storedDateAvailable, setStoredDateAvailable] = useState(dateAvailable);
  useEffect(() => {
    if (isSaveComplete) {
      save({
        // @ts-expect-error
        target: {
          name: fieldName,
          value: storedDateAvailable,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = async ({ target: { name: fieldName, value } }: React.ChangeEvent<HTMLInputElement>) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    // check validity before sending to BE
    if (!isValidDateStringFormatMMDDYYYY(value) && value !== '') {
      updateServerError('Date must be in MM/DD/YYYY format.', 'dateAvailable');
      return;
    }

    const formattedValue = value === '' || !isValid(new Date(value)) ? '' : format(new Date(value), 'yyyy-MM-dd');

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    try {
      if (!floorPlanId) {
        logError({
          errorType: 'FloorPlanIdMissingError',
          error: new Error('Missing floorPlanId'),
          context: `DateAvailableFormField.save: fieldName: ${fieldName}, floorplans: ${JSON.stringify(floorplans)}`,
        });
        throw new Error('Something went wrong. Refresh the page and try again.');
      }

      await updateDateAvailable({
        getParams: {
          floorPlanId,
          dateAvailable: formattedValue,
        },
      });

      const copyFloorplan = cloneDeep(floorplans);
      copyFloorplan[0][fieldName] = formattedValue;
      dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));

      // reset save cta
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateServerError(errorMessage, fieldName);
    } finally {
      wizardActions.updateIsFetchingData({
        reducerWizardDispatch,
        isFetchingData: false,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleDateChange = (newValue: string) => {
    clearServerError();
    setStoredDateAvailable(newValue);
  };

  return (
    <div data-test="DateAvailableFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <DatePickerWithDateSelect
        inputName={fieldName}
        onInputBlur={save}
        onDateChange={handleDateChange}
        initialDate={dateAvailable}
        showNextMonthButton={showNextMonthButton}
        setMaxDateOneYearAhead={true}
      />
    </div>
  );
};

export default DateAvailableFormField;
